import { useState, useEffect } from 'react';

import {
	performActionLocal,
	performActionServer,
	performAdminStateUpdateLocal,
	performAdminStateUpdateServer,
} from '@/action/action_controller';
import { logg } from '@/util/logg';
import { getPlayerStateWithId } from '@/util/player';
import { normalSeasonalTurn } from '@lc/const/turnTypes';
import { applyPatchCustom } from '@lc/lib/state';

import { handleStateChangeFeedback } from './GameEffects';
import { useSocketListeners } from './useSocketListeners';

//import { moveToHistoryIndex } from "./GameLogic";

export function useGameState() {
	const [enableSocket, setEnableSocket] = useState(false);
	const [lobbyId, setLobbyId] = useState('');
	const [gameId, setGameId] = useState(-1); // -1 = not set. Set when hitting a page not board3d
	const [gameStatus, setGameStatus] = useState(0); // 0 === running, 1 === lose, 2 === win

	const [userId, setUserId] = useState(-1);
	const [defaultName, setDefaultName] = useState('');
	const [defaultColor, setDefaultColor] = useState('');

	const [players, setPlayers] = useState([]);

	const [startGameBool, setStartGameBool] = useState(false);

	// GAME STATE IS EQUAL TO INITIAL STATE
	// TRANSFORMED BY STATECHANGEHISTORY
	// ACCORDING TO MOVE NUMBER && LIVE STATE VIEW
	// ALL STATES ARE DOWNSTREAM OF THIS

	const [initState, setInitState] = useState({});
	const [gameState, setGameState] = useState({});
	const [historyState, setHistoryState] = useState({});
	const [singlePlayer, setSinglePlayer] = useState(false);

	const [isTutorial, setIsTutorial] = useState(false);
	const [stepTutorial, setStepTutorial] = useState(0);

	const [liveStateView, setLiveStateView] = useState(true); // if false then viewing history state
	const [historyPos, setHistoryPos] = useState(-1); // keeps track of where we are at in the game, usually stateChangeHistory.length -1
	const [moveNumber, setMoveNumber] = useState(-1); // keeps track of what move we are in the game. Is our current index in stateChangeHistory
	const [stateChangeHistory, setStateChangeHistory] = useState([]);

	const [myState, setMyState] = useState({});

	const [playerStateArray, setPlayerStateArray] = useState([]);
	const [ticker, setTicker] = useState([]);
	const [lastTickerUpdate, setLastTickerUpdate] = useState([]);

	const [currPlayer, setCurrPlayer] = useState({});

	const [numPlayers, setNumPlayers] = useState(3);
	const [activePlayerId, setActivePlayerId] = useState(0);
	const [turnType, setTurnType] = useState(normalSeasonalTurn);
	const [monthTracker, setMonthTracker] = useState(0);
	const [seasonTracker, setSeasonTracker] = useState(0);
	const [carbonReservoir, setCarbonReservoir] = useState(25);

	const [serverDecisionPlayerId, setServerDecisionPlayerId] = useState(0);
	const [serverDecisionCardId, setServerDecisionCardId] = useState(0);
	const [serverDecisionChoiceIds, setServerDecisionChoiceIds] = useState([]);
	const [serverDecisionSourceIds, setServerDecisionSourceIds] = useState([]);
	const [serverDecisionTargetIds, setServerDecisionTargetIds] = useState([]);

	const [summitDraftCardIds, setSummitDraftCardIds] = useState([]); // 0 === running, 1 === lose, 2 === win
	const [summitWinners, setSummitWinners] = useState([]); // 0 === running, 1 === lose, 2 === win

	const [seasonalNewsCards, setSeasonalNewsCards] = useState([]);
	const [discardPile, setDiscardPile] = useState([]);

	/* RESET ALL OF THESE AFTER PLAYER CHOICES */
	const [selectedAction, setSelectedAction] = useState(0); // buyBattery, buyFactory, buyPR, playCard: (#cardId)
	const [selectedActionCost, setSelectedActionCost] = useState(0);
	const [selectedCard, setSelectedCard] = useState({});
	const [selectedCardId, setSelectedCardId] = useState(-1);

	const [paymentTotal, setPaymentTotal] = useState(0);
	const [paymentObject, setPaymentObject] = useState({
		factoryEnergyArr: [],
		batteryEnergyArr: [],
		carbonReserve: 0,
	});

	const [worldEnergy, setWorldEnergy] = useState({});

	/* END OF RESET BLOCK */

	const emptyObjCheck = {};

	// STATE CHANGE HAPPENS BY THE SENT PATCHES, WE DON'T JUST UPDATE TO GAMESTATE
	// Take in list of changes
	// Apply 1 by 1 to update the board with "animations"
	// Except if in a paused state
	const [lastProcessedMove, setLastProcessedMove] = useState(-1);

	useEffect(() => {
		if (!initState || !liveStateView || stateChangeHistory.length === 0)
			return;

		const newMoveNumber = stateChangeHistory.length - 1;

		let updatedState = structuredClone(initState);

		for (let i = 0; i <= newMoveNumber; i++) {
			if (stateChangeHistory[i]?.next_patch) {
				updatedState = applyPatchCustom(
					updatedState,
					stateChangeHistory[i].next_patch
				);
			}
		}

		// Collect all ticker messages since the last processed move
		const tickerQueue = [];

		for (let i = lastProcessedMove + 1; i <= newMoveNumber; i++) {
			const patch = stateChangeHistory[i];

			if (patch?.ticker?.length) {
				tickerQueue.push(...patch.ticker);
			}
		}

		tickerQueue.reverse();

		setMoveNumber(newMoveNumber);
		setGameState(updatedState);

		handleStateChangeFeedback(
			gameState,
			updatedState,
			stateChangeHistory[newMoveNumber]
		);

		if (tickerQueue.length) {
			console.log('tickerQueue', tickerQueue);
			setLastTickerUpdate(tickerQueue);
		}

		setLastProcessedMove(newMoveNumber);
	}, [stateChangeHistory]);

	useEffect(() => {
		if (stateChangeHistory.length > 0) {
			const tickerHist = [];
			for (let i = 0; i < stateChangeHistory.length; i++) {
				const tempTick = stateChangeHistory[i].ticker?.reverse() || [];
				tickerHist.push(...tempTick);
			}
			setTicker(tickerHist);
		}
	}, [stateChangeHistory]);

	useEffect(() => {
		if (gameState?.playerStateArray && currPlayer) {
			const myPlayerState = getPlayerStateWithId(
				gameState.playerStateArray,
				currPlayer.playerId
			);
			setMyState(myPlayerState);
			setPlayerStateArray(gameState.playerStateArray);
		}
	}, [gameState, currPlayer]);

	// THIS IS FINE SINCE WEEEE ARE EDITING GAMESTATE
	useEffect(() => {
		if (liveStateView) {
			logg('gameState updated');
			setMoveNumber(stateChangeHistory.length - 1);
			setUIHooksWithState(gameState);
		}
	}, [gameState]);

	useEffect(() => {
		if (!liveStateView) {
			logg('historyState updated');
			setUIHooksWithState(historyState);
		}
	}, [historyState]);

	//  useEffect(() => {
	//   logg("initState updated");
	//   setUIHooksWithState(initState);
	// }, [initState]);

	const moveForward = () => {
		logg('moveForward');
		logg('moveNumber');
		logg(moveNumber);
		logg('stateChangeHistory.length');
		logg(stateChangeHistory.length);
		if (moveNumber >= stateChangeHistory.length - 1) {
			setLiveStateView(true);
			return;
		}

		let updatedState = {};
		if (liveStateView) {
			updatedState = structuredClone(gameState);
		} else {
			updatedState = structuredClone(historyState);
		}

		updatedState = applyPatchCustom(
			updatedState,
			stateChangeHistory[moveNumber].next_patch
		);

		setLiveStateView(false);
		setMoveNumber((moveNumber) => moveNumber + 1);
		setHistoryState(updatedState);

		// handleStateChangeFeedback(prevState, updatedState, patch, setLastTickerUpdate);
	};

	const moveBackward = () => {
		logg('moveBackward');
		logg('moveNumber');
		logg(moveNumber);
		logg('stateChangeHistory.length');
		logg(stateChangeHistory.length);
		if (moveNumber <= 0) {
			return;
		}

		let updatedState = {};
		if (liveStateView) {
			updatedState = structuredClone(gameState);
		} else {
			updatedState = structuredClone(historyState);
		}

		logg('applyPatchCustom');
		logg('updatedState');
		logg(updatedState);
		logg('stateChangeHistory[moveNumber]');
		logg(stateChangeHistory[moveNumber]);
		logg('stateChangeHistory[moveNumber].prev_patch');
		logg(stateChangeHistory[moveNumber].prev_patch);

		updatedState = applyPatchCustom(
			updatedState,
			stateChangeHistory[moveNumber].prev_patch
		);

		setLiveStateView(false);
		setMoveNumber((moveNumber) => moveNumber - 1);
		setHistoryState(updatedState);

		// handleStateChangeFeedback(prevState, updatedState, patch, setLastTickerUpdate);
	};

	const resumeGame = () => {
		if (
			liveStateView === true ||
			moveNumber === stateChangeHistory.length
		) {
			return;
		}

		setMoveNumber(stateChangeHistory.length - 1);

		let updatedState = structuredClone(initState);
		for (let i = 0; i < stateChangeHistory.length; i++) {
			if (stateChangeHistory[i] && stateChangeHistory[i].next_patch) {
				updatedState = applyPatchCustom(
					updatedState,
					stateChangeHistory[i].next_patch
				);
			} else {
				logg('IT DON EVEN FOOKIN EXIST');
			}
		}

		setLiveStateView(true);
		setGameState(updatedState);
		setMoveNumber(stateChangeHistory.length - 1);
	};

	const setLiveView = () => setLiveStateView(!liveStateView);

	useSocketListeners({
		setPlayers,
		setInitState,
		setGameState,
		setStateChangeHistory,
		setStartGameBool,
		setSinglePlayer,
		setCurrPlayer,
		enabled: enableSocket,
	});

	function setUIHooksWithState(gameState) {
		if (gameState != null || gameState == emptyObjCheck) {
			const {
				numPlayers,
				activePlayerId,
				turnType,
				monthTracker,
				seasonTracker,
				carbonReservoir,
				worldEnergy,
				playerStateArray,
				seasonalNewsCards,
				discardPile,
				gameStatus,
				game_id_server,
				decisionPlayerId,
				decisionCardId,
				decisionChoiceArr,
				decisionSourceArr,
				decisionTargetArr,

				summitDraftCardIdForPlayer,
				summitWinners,
				stepTutorial,
			} = gameState;

			logg('gameId: ' + gameId + ' game_id_server: ' + game_id_server);

			// redundant now with the .join(roomId) concept
			if (game_id_server?.toString() !== gameId?.toString()) return; // prevent other games from updating this game

			setNumPlayers(numPlayers);
			setActivePlayerId(activePlayerId);
			setTurnType(turnType);
			setMonthTracker(monthTracker);
			setSeasonTracker(seasonTracker);
			setCarbonReservoir(carbonReservoir);
			setWorldEnergy(worldEnergy);
			setPlayerStateArray(playerStateArray); // setPlayerStateArray([...playerStateArray]);

			setSeasonalNewsCards(seasonalNewsCards);
			setDiscardPile(discardPile);

			setServerDecisionPlayerId(decisionPlayerId);
			setServerDecisionCardId(decisionCardId);
			setServerDecisionChoiceIds(decisionChoiceArr);
			setServerDecisionSourceIds(decisionSourceArr);
			setServerDecisionTargetIds(decisionTargetArr);

			setSummitDraftCardIds(summitDraftCardIdForPlayer);

			setSummitWinners(summitWinners);

			setGameStatus(gameStatus);

			setStepTutorial(stepTutorial || 0);

			setMyState(
				getPlayerStateWithId(playerStateArray, currPlayer.playerId)
			);
		}
	}

	async function performAction(action, obj) {
		logg('performAction', action);
		if (singlePlayer) {
			await performActionLocal(
				action,
				currPlayer,
				obj,
				gameId,
				gameState,
				stateChangeHistory,
				setStateChangeHistory
			);
		} else {
			await performActionServer(action, currPlayer, obj);
		}
	}

	async function performAdminStateUpdate(currPlayer, modalGameState) {
		if (singlePlayer) {
			await performAdminStateUpdateLocal(
				currPlayer,
				gameId,
				modalGameState,
				gameState,
				stateChangeHistory,
				setStateChangeHistory
			);
		} else {
			await performAdminStateUpdateServer(
				currPlayer,
				gameId,
				modalGameState
			);
		}
	}

	return {
		setEnableSocket,

		setLobbyId,
		lobbyId,

		setUserId,
		userId,

		setDefaultName,
		defaultName,
		setDefaultColor,
		defaultColor,

		gameState,
		setInitState,
		setStateChangeHistory,
		setStartGameBool,
		setSinglePlayer,

		currPlayer,
		numPlayers,
		setPlayers,
		activePlayerId,
		turnType,
		monthTracker,
		seasonTracker,
		carbonReservoir,
		worldEnergy,
		ticker,
		lastTickerUpdate,
		gameStatus,
		singlePlayer,

		startGameBool,
		setCurrPlayer,
		setGameId,

		liveStateView,
		setLiveStateView,

		historyPos,
		setHistoryPos,
		stateChangeHistory,

		players,
		playerStateArray,
		myState,

		setSeasonalNewsCards,
		seasonalNewsCards,
		discardPile,

		setSelectedAction,
		selectedAction,
		setSelectedActionCost,
		selectedActionCost,
		setSelectedCard,
		selectedCard,
		setSelectedCardId,
		selectedCardId,

		setPaymentTotal,
		paymentTotal,
		setPaymentObject,
		paymentObject,

		serverDecisionPlayerId,
		serverDecisionCardId,
		serverDecisionChoiceIds,
		serverDecisionSourceIds,
		serverDecisionTargetIds,

		summitDraftCardIds,

		summitWinners,

		setLiveView,

		moveForward,
		moveBackward,

		resumeGame,

		performAction,
		performAdminStateUpdate,

		setIsTutorial,
		isTutorial,
		setStepTutorial,
		stepTutorial,
	};
}

// If forward or backward, set live view to false
// walk back and forward 1 if possible (setting to now resumes play)
// const moveForward = () => moveToHistoryIndex(moveNumber + 1, {
//   initState,
//   stateChangeHistory,
//   setGameState,
//   setMoveNumber,
//   setLiveStateView,
//   gameState,
//   handleStateChangeFeedback,
//   setLastTickerUpdate,
// });

// const moveBackward = () => moveToHistoryIndex(moveNumber - 1, {
//   initState,
//   stateChangeHistory,
//   setGameState,
//   setMoveNumber,
//   setLiveStateView,
//   gameState,
//   handleStateChangeFeedback,
//   setLastTickerUpdate,
// });
